import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import type { IRecommendationEvent } from '@/interface/IBetBuilderRecommendation';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { eventResultScoreBetTypeGroupIds } from '@/libraries/oddsApi/betBuilderScoreSource';
import { formatRecommendationEvent } from '@/libraries/oddsApi/oddsApiFormatter';
import { useRootStore } from '@/stores/rootStore';
import {
    EnumOddsCategoryType,
    useRecommendationEventQuery as _useRecommendationEventQuery,
    useRecommendationEventSubscription,
} from '@/types';

export function useRecommendationEventQuery(
    eventId: MaybeRef<number>,
    isMixParlayMode: MaybeRef<boolean>,
    isEnabled: MaybeRef<boolean> = true,
) {
    const event = ref<IRecommendationEvent | null>(null);

    const variable = deepCompareComputed(() => ({
        query: {
            id: get(eventId),
            oddsCategory: get(isMixParlayMode) ? EnumOddsCategoryType.MixParlay : EnumOddsCategoryType.All,
        },
        betTypeGroupIds: eventResultScoreBetTypeGroupIds,
    }));

    const { isWsConnected } = storeToRefs(useRootStore());

    const pollInterval = deepCompareComputed(() => {
        if (isWsConnected.value) {
            return undefined;
        }
        return 30000;
    });

    const { onResult, refetch, loaded, error } = _useRecommendationEventQuery(
        variable,
        deepCompareComputed(() => ({
            enabled: get(isEnabled),
            pollInterval: get(pollInterval),
        })),
    );

    onResult((result) => {
        if (!result) {
            return;
        }

        event.value = formatRecommendationEvent(result);
    });

    const { onSubscription } = useRecommendationEventSubscription(
        variable,
        deepCompareComputed(() => ({
            enabled: get(isEnabled),
        })),
    );

    onSubscription((result) => {
        if (!result) {
            event.value = null;
            return;
        }

        event.value = formatRecommendationEvent(result);
    });

    return {
        event,
        loaded,
        refetch,
        error,
    };
}
